// Static promo copy derives display price from targetPrice. Use {price} or
// {targetPrice} in copy instead of hardcoding drop prices.

import { formatCompactUsd } from "@src/features/promotions/lib/dropDisplay"
import { NEAR_X_PROMOTIONAL_TERMS } from "@src/features/promotions/terms"

export type PromotionStatus = "active" | "upcoming" | "ended"

export type PromotionEligibilityRow = {
  label: string
  value: string
}

export type PromotionDropPreview = {
  poolSizeNear: number
  tvlThresholdUsd: number
  minConfidentialBalanceUsd: number
  minSwaps: number
}

export type Promotion = {
  id: string
  name: string
  status: PromotionStatus
  targetPrice: number
  /** Points API drop_id, correlating this entry with live `/drops` data. Omit
   *  until the drop exists in the Points API — the live panel stays hidden. */
  dropId?: number
  dropPreview?: PromotionDropPreview
  banner: {
    eyebrow: string
    headline: string
    description: string
    cta: { label: string; href: string }
  }
  howItWorks: string[]
  eligibility: PromotionEligibilityRow[]
  terms: string
  termsHref?: string
}

const PRICE_TOKEN = "{price}"
const TARGET_PRICE_TOKEN = "{targetPrice}"

function formatTargetPrice(targetPrice: number): string {
  return targetPrice.toFixed(2)
}

type PromotionContent = Omit<Promotion, "name" | "banner"> & {
  banner: Omit<Promotion["banner"], "headline">
}

type NearXDropContent = {
  id: string
  status: PromotionStatus
  targetPrice: number
  dropNumber: number
  poolSizeNear: number
  tvlThresholdUsd: number
  minConfidentialBalanceUsd: number
  minSwaps: number
  dropId?: number
  claimWindow?: string
}

const NEAR_X_BANNER_DESCRIPTION =
  "NEAR@X rewards growing your Confidential balance on near.com. Opt in to the program and your confidential activity earns non-transferable claim tokens that convert to NEAR when the price reaches {price} — the more you hold at snapshot, the larger your allocation."

const NEAR_X_HOW_IT_WORKS = [
  "Opt in to NEAR@{targetPrice} — points count from when you join.",
  "Grow your Confidential balance on near.com to earn points.",
  "We take periodic snapshots — the more you hold, the larger your allocation.",
  "Receive non-transferable NEAR@{targetPrice} claim tokens for your share.",
  "They convert 1:1 to NEAR once NEAR's price holds at or above {price}.",
]

function nearXDrop(content: NearXDropContent): PromotionContent {
  return {
    id: content.id,
    status: content.status,
    targetPrice: content.targetPrice,
    ...(content.dropId == null ? {} : { dropId: content.dropId }),
    dropPreview: {
      poolSizeNear: content.poolSizeNear,
      tvlThresholdUsd: content.tvlThresholdUsd,
      minConfidentialBalanceUsd: content.minConfidentialBalanceUsd,
      minSwaps: content.minSwaps,
    },
    banner: {
      eyebrow: `NEAR@X · Drop ${content.dropNumber}`,
      description: NEAR_X_BANNER_DESCRIPTION,
      cta: { label: "Move to Confidential", href: "/transfer" },
    },
    howItWorks: NEAR_X_HOW_IT_WORKS,
    eligibility: [
      {
        label: "Reward pool",
        value: `${content.poolSizeNear.toLocaleString("en-US")} NEAR`,
      },
      {
        label: "Global condition",
        value: `${formatCompactUsd(content.tvlThresholdUsd)} Confidential Intents TVL`,
      },
      {
        label: "Personal condition",
        value: `Over ${formatCompactUsd(content.minConfidentialBalanceUsd)} confidential balance · at least ${content.minSwaps} ${content.minSwaps === 1 ? "swap" : "swaps"}`,
      },
      {
        label: "Converts when",
        value: "VWAP ≥ {price} for 3 consecutive days",
      },
      {
        label: "Claim window",
        value: content.claimWindow ?? "30 days to claim your allocation",
      },
    ],
    terms: NEAR_X_PROMOTIONAL_TERMS,
    termsHref: "/terms",
  }
}

export const NEAR_X_PROMOTION_ID = "near-3.33"

const PROMOTIONS: readonly PromotionContent[] = [
  nearXDrop({
    id: NEAR_X_PROMOTION_ID,
    status: "active",
    targetPrice: 3.33,
    dropNumber: 1,
    dropId: 1,
    poolSizeNear: 333_333,
    tvlThresholdUsd: 70_000_000,
    minConfidentialBalanceUsd: 100,
    minSwaps: 1,
  }),
]

function resolvePromotion(content: PromotionContent): Promotion {
  const price = `$${formatTargetPrice(content.targetPrice)}`
  const targetPrice = formatTargetPrice(content.targetPrice)
  const fill = (text: string) =>
    text
      .replaceAll(PRICE_TOKEN, price)
      .replaceAll(TARGET_PRICE_TOKEN, targetPrice)
  const name = `NEAR@${formatTargetPrice(content.targetPrice)}`

  return {
    ...content,
    name,
    banner: {
      ...content.banner,
      headline: name,
      description: fill(content.banner.description),
    },
    howItWorks: content.howItWorks.map(fill),
    eligibility: content.eligibility.map((row) => ({
      ...row,
      value: fill(row.value),
    })),
    terms: fill(content.terms),
  }
}

export function getPromotions(status?: PromotionStatus): Promotion[] {
  const promotions = PROMOTIONS.map(resolvePromotion)
  return status ? promotions.filter((p) => p.status === status) : promotions
}

export function getPromotion(id: string): Promotion | undefined {
  const content = PROMOTIONS.find((p) => p.id === id)
  return content ? resolvePromotion(content) : undefined
}
